import { Dictionary, uniq } from 'lodash';
import { useCallback, useState } from 'react';
import { ResourcesToBeFetched } from 'api/resource-management/types';
import { logger } from 'services';
import { SchedulerResourceType } from '../resource-types';
import { GroupBy, SchedulerReference } from '../types';
import { getInitialResourcesToBeFetchedDictionary } from './common';
import { resourceIdForTeamHeader, resourceIdForUser } from './id';

const RESOURCES_BUFFER = 20;
const VISIBILITY_THRESHOLD = 0.75;

export const getInitialVisibleResourcesRange = () => ({
  firstIndex: 0,
  lastIndex: 0,
});

interface VisibleResourcesRange {
  firstIndex: number;
  lastIndex: number;
}

interface ReturnProps {
  getVisibleProjectIds: () => number[];
  getVisibleResources: () => ResourcesToBeFetched<Dictionary<boolean>>;
  getRoleIdsToBeFetched: () => Dictionary<boolean>;
  setCurrentVisibleResources: (range: VisibleResourcesRange) => void;
}

export const useVisibleResources = (
  schedulerRef: SchedulerReference,
  groupBy: GroupBy,
): ReturnProps => {
  const [currentVisibleResources, setCurrentVisibleResources] =
    useState<VisibleResourcesRange>(getInitialVisibleResourcesRange());

  const userResources = useCallback(
    () =>
      schedulerRef.current.instance.resources.filter(
        (resource) => resource.type === SchedulerResourceType.User,
      ),
    [schedulerRef],
  );

  const projectResources = useCallback(
    () =>
      schedulerRef.current.instance.resources.filter(
        (resource) => resource.type === SchedulerResourceType.Project,
      ),
    [schedulerRef],
  );

  const isTeamHeaderVisible = useCallback(
    () =>
      schedulerRef.current.instance.resources.filter(
        (resource) => resource.type === SchedulerResourceType.TeamHeader,
      ).length > 0,
    [schedulerRef],
  );

  const getFirstAndLastVisibleResources = useCallback(():
    | {
        firstVisibleResourceIndex: number;
        lastVisibleResourceIndex: number;
      }
    | undefined => {
    try {
      if (
        // TODO: to improve this check; sometimes still scheduler fails to get visible resources
        !schedulerRef.current ||
        schedulerRef.current.instance.resources.length === 0 ||
        !schedulerRef.current.instance.resourceStore.isSorted
      ) {
        throw new Error();
      }

      const {
        visibleResources: {
          first: firstVisibleResource,
          last: lastVisibleResource,
        },
      } = schedulerRef.current.instance;

      const isTeamHeaderPresent = isTeamHeaderVisible();

      let correctedFirstVisibleResource = firstVisibleResource;
      let correctedLastVisibleResource = lastVisibleResource;

      if (groupBy === GroupBy.Users) {
        const resourcesList = userResources();
        const [firstResource] = resourcesList;
        const lastResource = resourcesList[resourcesList.length - 1];

        switch (firstVisibleResource.type) {
          case SchedulerResourceType.TeamHeader:
            correctedFirstVisibleResource =
              firstVisibleResource?.nextSibling ?? firstResource;
            break;
          case SchedulerResourceType.ExpandableRole:
            correctedFirstVisibleResource =
              firstVisibleResource?.parent?.nextSibling ?? firstResource;
            break;
          case SchedulerResourceType.Team:
            correctedFirstVisibleResource =
              firstVisibleResource?.parent?.parent?.nextSibling ??
              firstResource;
            break;
          case SchedulerResourceType.User:
            break;
          case SchedulerResourceType.Project:
          case SchedulerResourceType.AssignProject:
            correctedFirstVisibleResource =
              firstVisibleResource?.parent ?? firstResource;
            break;
          default:
            correctedFirstVisibleResource = firstResource;
            break;
        }

        switch (lastVisibleResource.type) {
          case SchedulerResourceType.TeamHeader:
            // Ideally this would not happen
            correctedLastVisibleResource =
              lastVisibleResource?.nextSibling ?? firstResource;
            break;
          case SchedulerResourceType.ExpandableRole:
            // Ideally this also would not happen
            correctedLastVisibleResource =
              lastVisibleResource?.parent?.nextSibling ?? firstResource;
            break;
          case SchedulerResourceType.Team:
            correctedLastVisibleResource =
              lastVisibleResource?.parent?.parent?.nextSibling ?? firstResource;
            break;
          case SchedulerResourceType.User:
            break;
          case SchedulerResourceType.Project:
          case SchedulerResourceType.AssignProject:
            correctedLastVisibleResource =
              lastVisibleResource?.parent?.nextSibling ??
              lastVisibleResource?.parent ??
              lastResource;
            break;
          default:
            correctedLastVisibleResource = lastResource;
            break;
        }
      }

      if (groupBy === GroupBy.Projects) {
        const resourcesList = projectResources();
        const [firstResource] = resourcesList;
        const lastResource = resourcesList[resourcesList.length - 1];

        switch (firstVisibleResource.type) {
          case SchedulerResourceType.Project:
            break;
          case SchedulerResourceType.Role:
          case SchedulerResourceType.AssignUserOrPlaceholder:
            correctedFirstVisibleResource =
              firstVisibleResource?.parent ?? firstResource;
            break;
          case SchedulerResourceType.User:
          case SchedulerResourceType.Team:
            correctedFirstVisibleResource =
              firstVisibleResource?.parent?.parent ?? firstResource;
            break;
          default:
            correctedFirstVisibleResource = firstResource;
            break;
        }

        switch (lastVisibleResource.type) {
          case SchedulerResourceType.Project:
            break;
          case SchedulerResourceType.Role:
          case SchedulerResourceType.AssignUserOrPlaceholder:
            correctedLastVisibleResource =
              lastVisibleResource?.parent?.nextSibling ??
              lastVisibleResource?.parent ??
              lastResource;
            break;
          case SchedulerResourceType.User:
          case SchedulerResourceType.Team:
            correctedLastVisibleResource =
              lastVisibleResource?.parent?.parent?.nextSibling ??
              lastVisibleResource?.parent?.parent ??
              lastResource;
            break;
          default:
            correctedLastVisibleResource = lastResource;
            break;
        }
      }

      if (
        correctedFirstVisibleResource == null ||
        correctedLastVisibleResource == null
      ) {
        logger.log('** no visible users');

        return undefined;
      }

      const firstVisibleResourceIndex =
        correctedFirstVisibleResource.parentIndex -
        (isTeamHeaderPresent ? 1 : 0);
      const lastVisibleResourceIndex =
        correctedLastVisibleResource.parentIndex -
        (isTeamHeaderPresent ? 1 : 0);

      return {
        firstVisibleResourceIndex,
        lastVisibleResourceIndex,
      };
    } catch (e) {
      logger.log('Error when getting visibleResources from scheduler', e);

      return undefined;
    }
  }, [
    groupBy,
    isTeamHeaderVisible,
    projectResources,
    schedulerRef,
    userResources,
  ]);

  const getIndicesOfVisibleResources = useCallback(():
    | {
        startIndex: number;
        endIndex: number;
      }
    | undefined => {
    const firstAndLastVisibleResources = getFirstAndLastVisibleResources();

    if (firstAndLastVisibleResources == null) {
      return undefined;
    }

    const { firstVisibleResourceIndex, lastVisibleResourceIndex } =
      firstAndLastVisibleResources;

    const { firstIndex, lastIndex } = currentVisibleResources;
    const downwardThreshold =
      lastIndex === 0 // initialValue check
        ? 0
        : lastIndex + Math.ceil(RESOURCES_BUFFER * VISIBILITY_THRESHOLD);
    const upwardThreshold =
      firstIndex === 0
        ? 0
        : firstIndex - Math.ceil(RESOURCES_BUFFER * VISIBILITY_THRESHOLD);
    const scrollDirection =
      firstVisibleResourceIndex < firstIndex ? 'up' : 'down';

    if (
      scrollDirection === 'down'
        ? lastVisibleResourceIndex < downwardThreshold
        : firstVisibleResourceIndex > upwardThreshold
    ) {
      // TODO sometimes loaddaterange overrides scroll event; to check and handle if needed
      logger.log('** returning as load threshold not crossed');

      return undefined;
    }

    setCurrentVisibleResources({
      firstIndex: firstVisibleResourceIndex,
      lastIndex: lastVisibleResourceIndex,
    });

    const startIndex = Math.max(
      0,
      firstVisibleResourceIndex - RESOURCES_BUFFER,
    );
    const endIndex = Math.min(
      schedulerRef.current.instance.resources.length - 1,
      lastVisibleResourceIndex + RESOURCES_BUFFER,
    );

    return { startIndex, endIndex };
  }, [currentVisibleResources, getFirstAndLastVisibleResources, schedulerRef]);
  const getResourcesOfTypeUnderCompanies = useCallback(
    (resourceType: SchedulerResourceType) => {
      const partnerHeaderResource =
        schedulerRef.current.instance.resources.find(
          (resource) => resource.type === SchedulerResourceType.PartnerHeader,
        );

      if (partnerHeaderResource == null) {
        return [];
      }

      return partnerHeaderResource.children.reduce(
        (result, companyResource) => {
          if (
            companyResource == null ||
            companyResource.type !== SchedulerResourceType.Company
          ) {
            return result;
          }

          return result.concat(
            companyResource.children.filter(
              (resource) => resource.type === resourceType,
            ),
          );
        },
        [],
      );
    },
    [schedulerRef],
  );

  const getUserResourcesUnderCompanies = useCallback(() => {
    return getResourcesOfTypeUnderCompanies(SchedulerResourceType.User);
  }, [getResourcesOfTypeUnderCompanies]);
  const getExpandableRoleResourcesUnderCompanies = useCallback(() => {
    return getResourcesOfTypeUnderCompanies(
      SchedulerResourceType.ExpandableRole,
    );
  }, [getResourcesOfTypeUnderCompanies]);

  const getVisibleResources = useCallback((): ResourcesToBeFetched<
    Dictionary<boolean>
  > => {
    const visibleResourcesIndices = getIndicesOfVisibleResources();
    const visibleResources = getInitialResourcesToBeFetchedDictionary();

    if (visibleResourcesIndices == null) {
      return visibleResources;
    }
    if (groupBy === GroupBy.Users) {
      // make a change to filter only visible resource on the screen
      getUserResourcesUnderCompanies().forEach((resource) => {
        visibleResources.userIdList[resource.userId] = true;
      });
    }

    const { startIndex, endIndex } = visibleResourcesIndices;
    const resources =
      groupBy === GroupBy.Users ? userResources() : projectResources();

    resources.slice(startIndex, endIndex + 1).forEach((resource) => {
      if (groupBy === GroupBy.Users) {
        visibleResources.userIdList[resource.userId] = true;
      }
      if (groupBy === GroupBy.Projects) {
        visibleResources.projectIdList[resource.projectId] = true;
      }
    });

    return visibleResources;
  }, [
    getIndicesOfVisibleResources,
    getUserResourcesUnderCompanies,
    groupBy,
    projectResources,
    userResources,
  ]);

  const getVisibleProjectIds = useCallback(() => {
    const resources = getVisibleResources();

    if (groupBy === GroupBy.Projects) {
      const ids = Object.keys(resources.projectIdList).map((id) => Number(id));

      return ids;
    }
    if (groupBy === GroupBy.Users) {
      const getProjectIdsUnderUser = () => {
        return Object.keys(resources.userIdList).reduce(
          (projectIds, userId) => {
            const userResource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForUser(Number(userId)),
              );

            if (userResource != null) {
              return projectIds.concat(
                userResource.children.map((child) => child.get('projectId')),
              );
            }

            return projectIds;
          },
          [],
        );
      };
      const getProjectIdsUnderTeam = () => {
        const isTeamHeaderPresent = isTeamHeaderVisible();

        return isTeamHeaderPresent
          ? schedulerRef.current.instance.resourceStore.allRecords
              .filter(
                (resource) =>
                  resource.get('type') === SchedulerResourceType.Team &&
                  resource.get('teamId') != null &&
                  resource.get('projectId') != null,
              )
              .map((resource) => resource.get('projectId'))
          : [];
      };

      const ids = uniq(
        getProjectIdsUnderUser()
          .concat(getProjectIdsUnderTeam())
          .filter(Boolean),
      );

      return ids;
    }

    return [];
  }, [getVisibleResources, groupBy, isTeamHeaderVisible, schedulerRef]);

  const getRoleIdsToBeFetched = useCallback((): Dictionary<boolean> => {
    const roles: Dictionary<boolean> = {};

    try {
      if (
        // TODO: to improve this check; sometimes still scheduler fails to get visible resources
        !schedulerRef.current ||
        schedulerRef.current.instance.resources.length === 0 ||
        !schedulerRef.current.instance.resourceStore.isSorted
      ) {
        throw new Error();
      }

      getExpandableRoleResourcesUnderCompanies().forEach((resource) => {
        roles[resource.roleId] = true;
      });

      const teamHeader = schedulerRef.current?.instance.resourceStore.getById(
        resourceIdForTeamHeader(),
      );

      if (teamHeader != null) {
        teamHeader.children.forEach((child) => {
          if (child.type === SchedulerResourceType.ExpandableRole) {
            roles[child.roleId] = true;
          }
        });
      }
    } catch (e) {
      logger.log('Error when getting teamHeader from scheduler', e);
    }

    return roles;
  }, [getExpandableRoleResourcesUnderCompanies, schedulerRef]);

  return {
    getVisibleResources,
    getRoleIdsToBeFetched,
    setCurrentVisibleResources,
    getVisibleProjectIds,
  };
};
